import { endOfDay, isAfter, isBefore, isValid, startOfDay } from 'date-fns';

/**
 * The built-in field validation of @material-ui/pickers 3 (`validate()` in
 * `_helpers/text-field-helper`), restored at the wrapper level (prompt 2a-fix,
 * R-21; D-49). The order and the day-granular comparisons are v3's: an invalid
 * value first, then the maximum (`maxDate`, `disableFuture` against the end of
 * the day), then the minimum (`minDate`, `disablePast` against the start of the
 * day). `shouldDisableDate` is deliberately not part of it -- v3 never
 * validated the field against it, so a stored value the predicate rejects
 * today (an on-hold end date in the past) does not paint the field red.
 *
 * The strings are v3's defaults (`constants/prop-types`); no wrapper passed
 * `invalidDateMessage` / `minDateMessage` / `maxDateMessage` at the baseline
 * and no i18n key existed for them, so they stay literal.
 */
export const PICKER_VALIDATION_MESSAGES = {
  invalid: 'Invalid Date Format',
  min: 'Date should not be before minimal date',
  max: 'Date should not be after maximal date',
} as const;

export interface PickerValidationProps {
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
}

/** `''` when the value passes, as v3's `validationError` (the field's `error` is its truthiness). */
export const getPickerValidationError = (
  value: Date | null | undefined,
  { minDate, maxDate, disablePast, disableFuture }: PickerValidationProps,
  now: Date = new Date(),
): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (!isValid(value)) {
    return PICKER_VALIDATION_MESSAGES.invalid;
  }
  if (maxDate && isAfter(value, endOfDay(maxDate))) {
    return PICKER_VALIDATION_MESSAGES.max;
  }
  if (disableFuture && isAfter(value, endOfDay(now))) {
    return PICKER_VALIDATION_MESSAGES.max;
  }
  if (minDate && isBefore(value, startOfDay(minDate))) {
    return PICKER_VALIDATION_MESSAGES.min;
  }
  if (disablePast && isBefore(value, startOfDay(now))) {
    return PICKER_VALIDATION_MESSAGES.min;
  }
  return '';
};
